import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { ASSETS } from "@/components/landing/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    navigate("/admin", { replace: true });
    return null;
  }

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setNotice("If that email is registered, a reset link is on its way to your inbox.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-[#1B0F2E] border border-[#3B2760] text-[#F5EFE6] placeholder-[#A08FB8] focus:outline-none focus:border-[#FF2E88]";

  return (
    <div className="min-h-screen bg-[#1B0F2E] text-[#F5EFE6] flex items-center justify-center px-6">
      <div data-testid="admin-login-page" className="w-full max-w-sm bg-[#26153F] border border-[#3B2760] rounded-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <img src={ASSETS.logo} alt="Amanda's logo" className="w-16 h-16 rounded-xl object-cover mb-3" />
          <h1 className="display-type text-2xl font-bold uppercase">
            {mode === "login" ? "Owner Login" : "Reset Password"}
          </h1>
        </div>

        {mode === "login" ? (
          <form data-testid="login-form" onSubmit={submitLogin} className="space-y-4">
            <input
              data-testid="login-email-input"
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              data-testid="login-password-input"
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
            {error && <p data-testid="login-error" className="text-sm text-[#FF2E88]">{error}</p>}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-xl bg-[#FF2E88] text-white font-bold uppercase mono-lbl text-sm hover:bg-[#D61F73] transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
            <button
              data-testid="forgot-password-link"
              type="button"
              onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
              className="w-full text-center text-xs text-[#2DE2E6] hover:underline"
            >
              Forgot password?
            </button>
          </form>
        ) : (
          <form data-testid="forgot-form" onSubmit={submitForgot} className="space-y-4">
            <p className="text-xs text-[#A08FB8]">
              Enter your admin email and we&apos;ll send you a reset link.
            </p>
            <input
              data-testid="forgot-email-input"
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            {error && <p data-testid="forgot-error" className="text-sm text-[#FF2E88]">{error}</p>}
            {notice && <p data-testid="forgot-notice" className="text-sm text-[#2DE2E6]">{notice}</p>}
            <button
              data-testid="forgot-submit-button"
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-xl bg-[#FF2E88] text-white font-bold uppercase mono-lbl text-sm hover:bg-[#D61F73] transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : "Email Reset Link"}
            </button>
            <button
              data-testid="back-to-login-link"
              type="button"
              onClick={() => { setMode("login"); setError(""); setNotice(""); }}
              className="w-full text-center text-xs text-[#2DE2E6] hover:underline"
            >
              Back to sign in
            </button>
          </form>
        )}

        <Link to="/" data-testid="back-to-site-link" className="block text-center text-xs text-[#A08FB8] hover:text-[#F5EFE6] mt-6">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}
